<template>
  <v-container fluid class="pt-0">
    <v-card v-if="loading" class="text-center pa-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-4">Loading account data...</p>
    </v-card>

    <v-alert v-if="error" type="error" prominent>
      Could not load account data. Please try again later.
    </v-alert>

    <v-card v-if="accountUser && !loading && !error">
      <v-card-title class="d-flex align-center" id="user-title">
        <v-avatar color="primary" class="mr-3">
          <v-img v-if="accountUser.picture" :src="accountUser.picture" alt="User Avatar" id="user-img"></v-img>
          <span v-else-if="accountUser.name">{{ accountUser.name.charAt(0) }}</span>
          <v-icon v-else id="user-icon">fas fa-user</v-icon>
        </v-avatar>
        <span>{{ accountUser.name || accountUser.email }}</span>
        <v-spacer></v-spacer>
        <v-chip :color="accountUser.account_status === 'Active' ? 'green' : accountUser.account_status === 'Pending' ? 'orange' : 'red'" dark>{{ accountUser.account_status }}</v-chip>
      </v-card-title>
      <v-divider></v-divider>

      <v-list dense>
        <v-list-item>
          <v-list-item-title>Email</v-list-item-title>
          <v-list-item-subtitle>{{ accountUser.email }}</v-list-item-subtitle>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Subscription Level</v-list-item-title>
          <v-list-item-subtitle>{{ subscriptionLevel }}</v-list-item-subtitle>
        </v-list-item>

        <v-list-item v-if="showTrueDepthBillingNote">
          <v-list-item-title></v-list-item-title>
          <v-list-item-subtitle class="text-orange-lighten-1">
            Note: TrueDepth Usage will be billed separately.
          </v-list-item-subtitle>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Account Status</v-list-item-title>
          <v-list-item-subtitle>
            <v-chip :color="accountUser.account_status === 'Active' ? 'green' : accountUser.account_status === 'Pending' ? 'orange' : 'red'" dark>{{ accountUser.account_status }}</v-chip>
          </v-list-item-subtitle>
        </v-list-item>

        <v-list-item v-if="accountUser.specialFilePermissions && accountUser.specialFilePermissions.length">
          <v-list-item-title>Special File Permissions</v-list-item-title>
          <v-list-item-subtitle>
            <div class="d-flex flex-wrap align-center">
              <v-chip v-for="permissionName in accountUser.specialFilePermissions" :key="permissionName" small class="ma-1">{{ permissionName }}</v-chip>
            </div>
          </v-list-item-subtitle>
        </v-list-item>

        <v-divider v-if="billingAddress"></v-divider>

        <v-list-item v-if="billingAddress">
          <v-list-item-title>Billing Address</v-list-item-title>
          <v-list-item-subtitle>
            {{ billingAddress.street_1 }}<br>
            <span v-if="billingAddress.street_2">{{ billingAddress.street_2 }}<br></span>
            {{ billingAddress.city }}, {{ billingAddress.state }} {{ billingAddress.postal_code }}<br>
            {{ countryName }}<br v-if="billingAddress.vat_number || billingAddress.billing_email" />
            <span v-if="billingAddress.vat_number">VAT: {{ billingAddress.vat_number }}<br v-if="billingAddress.billing_email" /></span>
            <span v-if="billingAddress.billing_email">Billing Email: {{ billingAddress.billing_email }}</span>
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-btn data-testid="edit-profile-btn" @click="isEditProfileDialogOpen = true">Edit Profile</v-btn>
        <v-btn data-testid="edit-billing-address-btn" v-if="billingAddress" @click="openEditBillingDialog">Edit Billing Address</v-btn>
        <v-btn data-testid="change-password-btn" v-if="auth0User && !auth0User.sub.startsWith('google-oauth2')" @click="requestPasswordChange">Change Password</v-btn>
        <v-btn
          v-if="!isOrgMember"
          color="primary"
          variant="tonal"
          prepend-icon="fas fa-sitemap"
          :to="{ name: 'OrgCreationView' }"
          data-testid="create-org-btn"
        >Create Organization</v-btn>
        <v-spacer></v-spacer>
        <div v-if="isManualSubscription" class="text-right">
          <p class="text-body-2">
            To manage your Enterprise subscription, please
            <a :href="`mailto:${SUPPORT_EMAIL}`">contact support</a>.
          </p>
        </div>
        <div v-else-if="showManageSubscriptionButton" class="text-right">
          <v-alert v-if="subscriptionManagementError" type="error" dense text class="mb-2">
            {{ subscriptionManagementError }}
          </v-alert>
          <v-btn
            class="str-btn-primary"
            @click="manageSubscription"
            :loading="isManagingSubscription"
            :disabled="isManagingSubscription"
          >
            Manage Subscription
          </v-btn>
        </div>
      </v-card-actions>

      <v-divider v-if="showAddCaptureButton || showAddSdkButton"></v-divider>
      <v-card-actions v-if="showAddCaptureButton || showAddSdkButton" class="pa-4">
        <v-btn
          v-if="showAddCaptureButton"
          data-testid="add-capture-btn"
          color="primary"
          variant="outlined"
          @click="router.push({ name: 'AddCaptureProduct' })"
        >
          Add Structure Capture Enterprise
        </v-btn>
        <v-btn
          v-if="showAddSdkButton"
          data-testid="add-sdk-btn"
          color="primary"
          variant="outlined"
          @click="router.push({ name: 'AddSdkProduct' })"
        >
          Add SDK Access
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Edit Profile Dialog -->
    <v-dialog v-model="isEditProfileDialogOpen" max-width="500px">
      <v-card data-testid="edit-profile-card">
        <v-card-title>Edit Profile</v-card-title>
        <v-card-text>
          <v-text-field data-testid="profile-email-input" v-model="editableProfile.email" label="Email" :disabled="auth0User.sub.startsWith('google-oauth2')"></v-text-field>
           <v-alert v-if="auth0User.sub.startsWith('google-oauth2')" type="info" dense text>
            Email cannot be changed for Google accounts.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="isEditProfileDialogOpen = false">Cancel</v-btn>
          <v-btn data-testid="save-profile-btn" color="primary" @click="saveProfile">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Billing Address Dialog -->
    <v-dialog v-model="isEditBillingDialogOpen" max-width="600px">
      <v-card data-testid="edit-billing-card">
        <v-card-title>Edit Billing Address</v-card-title>
        <v-card-text>
            <v-text-field data-testid="billing-street1-input" v-model="editableBillingAddress.street_1" label="Address Line 1" required></v-text-field>
            <v-text-field data-testid="billing-street2-input" v-model="editableBillingAddress.street_2" label="Address Line 2"></v-text-field>
            <v-text-field data-testid="billing-city-input" v-model="editableBillingAddress.city" label="City" required></v-text-field>
            <v-select
              data-testid="billing-country-select"
              v-model="editableBillingAddress.country"
              :items="countries"
              item-title="name"
              item-value="isoCode"
              label="Country"
              required
            ></v-select>
            <v-select
              data-testid="billing-state-select"
              v-model="editableBillingAddress.state"
              :items="states"
              item-title="name"
              item-value="isoCode"
              label="State"
              required
              :disabled="!editableBillingAddress.country"
            ></v-select>
            <v-text-field data-testid="billing-postal-code-input" v-model="editableBillingAddress.postal_code" label="Postal Code" required></v-text-field>
            <v-text-field data-testid="billing-vat-input" v-model="editableBillingAddress.vat_number" label="VAT Number"></v-text-field>
            <v-text-field data-testid="billing-email-input" v-model="editableBillingAddress.billing_email" label="Billing Email (Optional)"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="isEditBillingDialogOpen = false">Cancel</v-btn>
          <v-btn data-testid="save-billing-address-btn" color="primary" @click="saveBillingAddress">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn text @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Country, State } from 'country-state-city';

const { getAccessTokenSilently, user: auth0User } = useAuth0();
const router = useRouter();
const store = useStore();
const isOrgMember = computed(() => store.getters.isOrgMember);
const accountUser = ref(null);
const loading = ref(true);
const error = ref(false);
const isManagingSubscription = ref(false);
const subscriptionManagementError = ref(null);
const SUPPORT_EMAIL = 'developers@structure.io';
const billingAddress = ref(null);

const isEditProfileDialogOpen = ref(false);
const editableProfile = ref({ name: '', email: '' });

const isEditBillingDialogOpen = ref(false);
const editableBillingAddress = ref({});
const snackbar = ref(false);
const snackbarText = ref('');
const snackbarColor = ref('success');

const countries = ref(Country.getAllCountries());
const states = ref([]);

watch(auth0User, (newUser) => {
  if (newUser) {
    editableProfile.value = {
      email: newUser.email || ''
    };
  }
}, { immediate: true });

const openEditBillingDialog = () => {
  editableBillingAddress.value = { ...billingAddress.value };
  if (editableBillingAddress.value.country) {
    states.value = State.getStatesOfCountry(editableBillingAddress.value.country);
  }
  isEditBillingDialogOpen.value = true;
};

watch(() => editableBillingAddress.value.country, (newCountryCode, oldCountryCode) => {
  if (newCountryCode) {
    states.value = State.getStatesOfCountry(newCountryCode);
    if (newCountryCode !== oldCountryCode) {
      editableBillingAddress.value.state = ''; // Clear state when country changes
    }
  } else {
    states.value = [];
    editableBillingAddress.value.state = '';
  }
});

const saveProfile = async () => {
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };
    await axios.patch(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/users/${auth0User.value.sub}`,
      editableProfile.value,
      { headers }
    );
    // Refresh user data to show updated info
    accountUser.value.name = editableProfile.value.email;
    accountUser.value.email = editableProfile.value.email;
    isEditProfileDialogOpen.value = false;
    snackbarText.value = 'Profile updated successfully!';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (err) {
    console.error('Error updating profile:', err);
    snackbarText.value = 'Error updating profile.';
    snackbarColor.value = 'error';
    snackbar.value = true;
    // You might want to show an error message to the user
  }
};

const saveBillingAddress = async () => {
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    const nameParts = (accountUser.value.name || '').split(' ');
    const firstName = nameParts.length > 0 ? nameParts[0] : '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    const payload = {
      auth0_id: auth0User.value.sub,
      address_line_1: editableBillingAddress.value.street_1,
      address_line_2: editableBillingAddress.value.street_2,
      city: editableBillingAddress.value.city,
      state: editableBillingAddress.value.state,
      country: editableBillingAddress.value.country,
      zip_code: editableBillingAddress.value.postal_code,
      vat_number: editableBillingAddress.value.vat_number,
      billing_email: editableBillingAddress.value.billing_email,
      first_name: firstName,
      last_name: lastName,
      organization: accountUser.value.organization || '',
    };

    await axios.post(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/address/${auth0User.value.sub}`,
      payload,
      { headers }
    );

    billingAddress.value = { ...editableBillingAddress.value };
    isEditBillingDialogOpen.value = false;
    snackbarText.value = 'Billing address updated successfully!';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (err) {
    console.error('Error updating billing address:', err);
    snackbarColor.value = 'error';
    snackbarText.value = 'Error updating billing address.';
    snackbar.value = true;
  }
};

const requestPasswordChange = async () => {
  try {
    await axios.post(`https://${process.env.VUE_APP_AUTH0_DOMAIN}/dbconnections/change_password`, {
      client_id: process.env.VUE_APP_AUTH0_CLIENT_ID,
      email: accountUser.value.email,
      connection: 'Username-Password-Authentication',
    });
    snackbarText.value = 'Password reset email sent successfully!';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (err) {
    console.error('Error requesting password change:', err);
    snackbarText.value = 'Failed to send password reset email.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  }
};

const tierMap = {
  'Basic': 'Basic',
  'Professional': 'Professional',
  'Premium': 'Premium',
  'Enterprise': 'Enterprise',
};

const subscriptionLevel = computed(() => {
  if (accountUser.value?.tier) {
    return tierMap[accountUser.value.tier] || 'Unknown';
  }
  if (accountUser.value?.customer_type === 'capture') {
    return 'Structure Capture Enterprise';
  }
  return 'N/A';
});

const showTrueDepthBillingNote = computed(() => {
  return accountUser.value && accountUser.value.tier === 'Premium';
});

const isManualSubscription = computed(() => {
  return accountUser.value && accountUser.value.tier === 'Enterprise';
});

const showManageSubscriptionButton = computed(() => {
  if (!accountUser.value || !accountUser.value.tier) {
    return false;
  }
  const stripeTiers = ['Basic', 'Professional', 'Premium'];
  return stripeTiers.includes(accountUser.value.tier);
});

const showAddCaptureButton = computed(() =>
  accountUser.value?.customer_type === 'sdk' &&
  accountUser.value?.account_status === 'Active'
);

const showAddSdkButton = computed(() =>
  accountUser.value?.customer_type === 'capture' &&
  accountUser.value?.account_status === 'Active'
);

const countryName = computed(() => {
  if (billingAddress.value && billingAddress.value.country) {
    const countryInfo = Country.getCountryByCode(billingAddress.value.country);
    return countryInfo ? countryInfo.name : billingAddress.value.country;
  }
  return '';
});

const manageSubscription = async () => {
  isManagingSubscription.value = true;
  error.value = false;
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    const response = await axios.post(
      `${process.env.VUE_APP_CBE_BASE_URL}/stripe/customer-portal`,
      {},
      { headers }
    );

    const { url } = response.data;
    if (url) {
      window.location.href = url;
    } else {
      error.value = true;
      console.error('Customer portal URL not found in response.');
    }
  } catch (e) {
    error.value = true;
    subscriptionManagementError.value = 'Could not redirect to the customer portal. Please try again later.';
    console.error('Error redirecting to customer portal:', e);
  } finally {
    isManagingSubscription.value = false;
  }
};

onMounted(async () => {
  loading.value = true;
  error.value = false;
  try {
    const token = await getAccessTokenSilently();
    const auth0Id = auth0User.value.sub;
    const headers = { Authorization: `Bearer ${token}` };

    // Fetch user data from backend
    const userResponse = await axios.get(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/users/${auth0Id}`,
      { headers }
    );
    const backendUserData = userResponse.data;

    // Fetch billing address
    try {
      const billingResponse = await axios.get(
        `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/address/${auth0Id}`,
        { headers }
      );
      billingAddress.value = billingResponse.data;
    } catch (billingError) {
      if (billingError.response && billingError.response.status !== 404) {
        console.error('Error fetching billing address:', billingError);
      }
    }

    // NEW: Fetch special files data
    let specialFilesNames = [];
    try {
      const specialFilesResponse = await axios.get(
        `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/specialfiles/${auth0Id}`,
        { headers }
      );
      // Assuming specialFilesResponse.data is an array of objects with an 'app' property
      specialFilesNames = specialFilesResponse.data.map(file => file.app);
    } catch (specialFilesError) {
      console.error('Error fetching special files:', specialFilesError);
      // Continue without special files if there's an error
    }

          // Combine Auth0 user data and backend user data
        accountUser.value = {
          name: auth0User.value.name || auth0User.value.email,
          email: auth0User.value.email,
          picture: auth0User.value.picture,
          tier: backendUserData.tier,
          customer_type: backendUserData.customer_type,
          account_status: backendUserData.account_status,
          organization: backendUserData.organization,
          specialFilePermissions: specialFilesNames,
        };
  } catch (e) {
    error.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/AccountView.scss'
@import '@/assets/styles/StrButtons.scss'
</style>